import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/encaissements';
import permissionsApi from '../services/permissions';
import EncaissementModal, { fmtEur, fmtDateHeure } from './EncaissementModal';
import EncaissementDetail, { STATUT_BADGE } from './EncaissementDetail';
import EncaissementsConfig from './EncaissementsConfig';
import './Encaissements.css';

// Encaissements Adyen — acomptes et règlements encaissés depuis le CRM, sur le
// TPE de l'agence ou par lien de paiement. La liste est le point d'entrée de
// la comptabilité : filtres par société / statut / période, export CSV aligné
// sur les références Adyen (pspReference, ENC-<id>).

const STATUTS_FILTRE = [
  ['', 'Tous les statuts'], ['paye', 'Payés'], ['en_attente_tpe,lien_envoye', 'En cours'], ['a_verifier', 'À vérifier'],
  ['brouillon', 'Brouillons'], ['refuse,annule,expire', 'Refusés / annulés / expirés'],
];

function Encaissements({ user, setHeaderAction }) {
  const [onglet, setOnglet] = useState('liste');
  const [droits, setDroits] = useState({ peut_creer: false, peut_modifier: false });
  const [contexte, setContexte] = useState(null);
  const [societes, setSocietes] = useState([]);
  const [liste, setListe] = useState([]);
  const [stats, setStats] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtres, setFiltres] = useState({ q: '', statut: '', codsoc: '', canal: '', du: '', au: '', alerte: '' });
  const [modal, setModal] = useState(null);      // null | { encaissement?: obj }
  const [detail, setDetail] = useState(null);
  const isSuperAdmin = user?.role === 'super-admin';

  useEffect(() => {
    permissionsApi.checkAccess('encaissements').then(({ data }) => setDroits(data.permissions || {})).catch(() => {});
    api.contexte().then(({ data }) => setContexte(data)).catch(() => {});
    api.societes().then(({ data }) => setSocietes(data.societes || [])).catch(() => {});
  }, []);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filtres).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.liste(params);
      setListe(data.encaissements || []); setStats(data.stats || []); setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [filtres]);
  useEffect(() => { charger(); }, [charger]);

  // Une ligne « sur le TPE » ou « lien envoyé » peut changer sans nous : on
  // rafraîchit doucement tant qu'il y en a.
  useEffect(() => {
    if (!liste.some((e) => ['en_attente_tpe', 'a_verifier'].includes(e.statut))) return undefined;
    const t = setTimeout(charger, 5000);
    return () => clearTimeout(t);
  }, [liste, charger]);

  const exporter = useCallback(async () => {
    const params = {};
    Object.entries(filtres).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.exportCsv(params);
    const url = URL.createObjectURL(new Blob([data], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = `encaissements-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }, [filtres]);

  useEffect(() => {
    if (!setHeaderAction) return;
    if (onglet !== 'liste') { setHeaderAction(null); return; }
    setHeaderAction({
      primary: droits.peut_creer ? { label: '+ Nouvel encaissement', onClick: () => setModal({}) } : null,
      secondary: [{
        label: (<><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Export CSV</>),
        onClick: exporter,
      }],
    });
  }, [setHeaderAction, onglet, droits.peut_creer, exporter]);

  const stat = (s) => stats.filter((x) => s.includes(x.statut)).reduce((acc, x) => ({ n: acc.n + x.n, m: acc.m + Number(x.montant_cents) }), { n: 0, m: 0 });
  const payes = stat(['paye']); const enCours = stat(['en_attente_tpe', 'lien_envoye']); const aVerifier = stat(['a_verifier']);
  const codsocs = [...new Map(societes.map((s) => [s.codsoc, s])).values()];
  const set = (k) => (e) => setFiltres((f) => ({ ...f, [k]: e.target.value }));
  const majLigne = (e) => setListe((l) => (l.some((x) => x.id === e.id) ? l.map((x) => (x.id === e.id ? { ...x, ...e } : x)) : [e, ...l]));

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={onglet === 'liste' ? 'active' : ''} onClick={() => setOnglet('liste')}>📋 Encaissements</button>
        {isSuperAdmin && <button className={onglet === 'config' ? 'active' : ''} onClick={() => setOnglet('config')}>⚙️ Configuration</button>}
      </div>

      {onglet === 'config' && <EncaissementsConfig />}

      {onglet === 'liste' && (
        <>
          {contexte && !contexte.adyen_configure && (
            <div className="status-warning enc-bandeau">Adyen n'est pas configuré sur cette instance : les encaissements restent en brouillon. {isSuperAdmin ? 'Voir l\'onglet Configuration.' : ''}</div>
          )}
          {contexte && contexte.adyen_configure && contexte.comptes_actifs === 0 && (
            <div className="status-warning enc-bandeau">Aucun merchant account Adyen n'est rattaché à une agence : impossible d'encaisser. {isSuperAdmin ? 'Renseignez-les dans Configuration.' : 'Demandez à un administrateur.'}</div>
          )}
          {contexte && contexte.env === 'test' && contexte.adyen_configure && (
            <div className="status-info enc-bandeau">🧪 Environnement Adyen <b>TEST</b> : aucun paiement réel n'est encaissé.</div>
          )}

          <div className="enc-kpis">
            <div className="enc-kpi enc-kpi-ok"><span>{fmtEur(payes.m)}</span>{payes.n} payé{payes.n > 1 ? 's' : ''}</div>
            <div className="enc-kpi"><span>{enCours.n}</span>en cours ({fmtEur(enCours.m)})</div>
            <div className={`enc-kpi ${aVerifier.n ? 'enc-kpi-alerte' : ''}`}><span>{aVerifier.n}</span>à vérifier</div>
            <div className="enc-kpi"><span>{total}</span>au total</div>
          </div>

          <div className="page-toolbar">
            <input className="page-search" placeholder="🔍 Rechercher..." value={filtres.q} onChange={set('q')} />
            <select className={`page-select${filtres.statut ? ' active-filter' : ''}`} value={filtres.statut} onChange={set('statut')}>
              {STATUTS_FILTRE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {codsocs.length > 1 && (
              <select className={`page-select${filtres.codsoc ? ' active-filter' : ''}`} value={filtres.codsoc} onChange={set('codsoc')}>
                <option value="">Toutes les sociétés</option>
                {codsocs.map((s) => <option key={s.codsoc} value={s.codsoc}>{s.codsoc} — {s.nom}</option>)}
              </select>
            )}
            <select className={`page-select${filtres.canal ? ' active-filter' : ''}`} value={filtres.canal} onChange={set('canal')}>
              <option value="">TPE et liens</option><option value="terminal">TPE</option><option value="lien">Liens</option>
            </select>
            <input type="date" className={`page-select${filtres.du ? ' active-filter' : ''}`} value={filtres.du} onChange={set('du')} title="Du" />
            <input type="date" className={`page-select${filtres.au ? ' active-filter' : ''}`} value={filtres.au} onChange={set('au')} title="Au" />
            <select className={`page-select${filtres.alerte ? ' active-filter' : ''}`} value={filtres.alerte} onChange={set('alerte')}>
              <option value="">Avec ou sans alerte</option><option value="1">⚠️ Alertes seulement</option>
            </select>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr><th>Date</th><th>Référence</th><th>Client</th><th>Facture / BDC</th><th>Société</th><th style={{ textAlign: 'right' }}>Montant TTC</th><th>Canal</th><th>Statut</th><th>Vendeur</th><th>Réf. Adyen</th><th></th></tr>
              </thead>
              <tbody>
                {loading && !liste.length && <tr><td colSpan={11} style={{ color: 'var(--text-muted)' }}>Chargement…</td></tr>}
                {!loading && !liste.length && <tr><td colSpan={11} style={{ color: 'var(--text-muted)' }}>Aucun encaissement{Object.values(filtres).some(Boolean) ? ' pour ces filtres' : ''}.</td></tr>}
                {liste.map((e) => {
                  const b = STATUT_BADGE[e.statut] || { label: e.statut, classe: 'status-info' };
                  return (
                    <tr key={e.id} className="enc-ligne-cliquable" onClick={() => setDetail(e)}>
                      <td>{fmtDateHeure(e.created_at)}</td>
                      <td className="enc-ref">{e.reference}</td>
                      <td className="enc-client">{e.client_prenom} {e.client_nom}{e.client_code && <small>code {e.client_code}</small>}</td>
                      <td>{e.numero_bdc ? `BDC ${e.numero_bdc}` : ''}{e.numero_bdc && e.numero_facture ? ' · ' : ''}{e.numero_facture ? `Fac. ${e.numero_facture}` : ''}</td>
                      <td>{e.codsoc}<small style={{ display: 'block', color: 'var(--text-muted)', fontSize: 12 }}>{e.concession_nom}</small></td>
                      <td className="enc-montant">{fmtEur(e.montant_cents)}</td>
                      <td>{e.canal === 'terminal' ? `🖥️ ${e.terminal_libelle || 'TPE'}` : e.canal === 'lien' ? '🔗 Lien' : '—'}</td>
                      <td><span className={b.classe}>{b.label}</span>{e.alerte && <span className="enc-alerte" title={e.refus_motif || 'Alerte'}>⚠️</span>}</td>
                      <td>{[e.vendeur_prenom, e.vendeur_nom].filter(Boolean).join(' ')}</td>
                      <td className="enc-ref">{e.psp_reference || ''}</td>
                      <td><div className="action-buttons"><button className="btn-icon" title="Ouvrir" onClick={(ev) => { ev.stopPropagation(); setDetail(e); }}>👁️</button></div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modal && (
        <EncaissementModal
          user={user} contexte={contexte} societes={societes} encaissement={modal.encaissement || null}
          onClose={() => { setModal(null); charger(); }}
          onChange={majLigne}
        />
      )}
      {detail && (
        <EncaissementDetail
          user={user} encaissement={detail}
          onClose={() => { setDetail(null); charger(); }}
          onChange={majLigne}
          onRelancer={(e) => { setDetail(null); setModal({ encaissement: e }); }}
        />
      )}
    </div>
  );
}

export default Encaissements;
